import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import InstanceApi from "../../api/instance/InstanceApi";
import TableCSVExport from "../../component/TableCSVExport";
import { useTable, useSortBy, usePagination, Column } from "react-table";
import PageContent from "../../container/page_content";
import Breadcrumb from "../../container/breadcrumb";
import { Link } from "react-router-dom";
import TablePagination from "../../component/TablePagination";
import PieChart from "../../component/PieChart";
import BarChart from "../../component/BarChart";
import NoData from "../../component/NoData";
import ConditionalRender from "../../helpers/ConditionalRender";
import FilterButtonGroup from "../../component/FilterButtonGroup";
import FilterPanel from "../../component/FilterPanel";
import SearchInput from "../../component/SearchInput";

// Hooks
import { useInstanceFilters } from "../../hooks/useInstanceFilters";
import { useInstanceTargets } from "../../context/InstanceContext";

// Type mapping for join
const DB_TYPE_MAP: Record<string, string> = {
    mysql: "MySQL",
    mariadb: "MariaDB",
    postgresql: "PostgreSQL",
    cockroachdb: "CockroachDB",
    sqlserver: "Microsoft SQL Server",
    db2: "Db2 LUW",
    oracle: "Oracle Database",
    informix: "Informix Server",
    saphana: "SAP HANA Database",
    sybase: "SAP ASE Database",
    mongodb: "MongoDB"
};

// Helper to normalize SQL Server labels for matching (removes CU, GDR, RTM, nicknames)
function getSqlServerBaseLabel(label: string): string {
    return label
            .replace(/\s+CU\d+.*/i, "")
            .replace(/\s+GDR.*/i, "")
            .replace(/\s+RTM.*/i, "")
            .replace(/\s+'[^']*'/g, "")
            .trim();
}

// Helper to find EOL entry for SQL Server with fallback strategies
function findSqlServerEolEntry(eolData: any[], baseLabel: string, majorVersion: string, spSuffix: string): any {
    const versionToMatch = majorVersion + spSuffix;

    return eolData.find(v => v.label && getSqlServerBaseLabel(v.label) === baseLabel)
            || (versionToMatch && eolData.find(v => v.version === versionToMatch))
            || (majorVersion && !spSuffix && eolData.find(v => v.version === majorVersion))
            || null;
}

function normalizeVersion(type: string, version: string): string {
    if (!version || typeof version !== "string") return "";

    const normalizers: Record<string, (v: string) => string> = {
        db2: (v) => {
            let clean = v.replace(/^DB2\s+/i, "").replace(/^v/i, "").replace(/^[^\d]*/, "");
            const parts = clean.split(".");
            return parts.length >= 2 ? `${parts[0]}.${parts[1]}` : clean;
        },
        "db2 luw": (v) => normalizers.db2(v),

        informix: (v) => v.match(/^(\d+\.\d+)/)?.[1] ?? v,
        "informix server": (v) => normalizers.informix(v),

        saphana: (v) => v.replace(/\.\d+$/, ".xxxxxxxx"),
        "sap hana database": (v) => normalizers.saphana(v),

        sybase: (v) => v.match(/^(\d+\.\d+)/)?.[1] ?? v,
        "sap ase database": (v) => normalizers.sybase(v),

        mariadb: (v) => v.match(/^(\d+\.\d+)/)?.[1] ?? v,
        mysql: (v) => /-MariaDB$/i.test(v)
                ? v.replace(/-MariaDB$/i, "").match(/^(\d+\.\d+)/)?.[1] ?? v
                : v.match(/^(\d+\.\d+)/)?.[1] ?? v,

        cockroachdb: (v) => v.match(/^(\d+\.\d+)/)?.[1] ?? v,

        oracle: (v) => v.match(/^(\d+)/)?.[1] ?? v,
        sqlserver: (v) => v.match(/^(\d{4})/)?.[1] ?? v,
        postgresql: (v) => v.match(/^(\d+(\.\d+)?)/)?.[1] ?? v,
        mongodb: (v) => {
            const parts = v.split(".");
            return parts.length >= 2 ? `${parts[0]}.${parts[1]}` : v;
        }
    };

    const key = type.toLowerCase();
    return normalizers[key]?.(version) ?? version.match(/^(\d+)/)?.[1] ?? version;
}

const DBVersionsList: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [maintainedFilter, setMaintainedFilter] = useState("all");
    const [selectedDbType, setSelectedDbType] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Memoized callbacks to prevent chart re-renders
    const noOpSetFilterOptions = useCallback(() => {}, []);
    const handleDbTypeSliceClick = useCallback((point: any) => {
        if (point && point.name) {
            setSelectedDbType(prev => point.name === prev ? null : point.name);
        }
    }, []);

    // State for fetched_at date from dbversions.netlify.app
    const [dbEolFetchedAt, setDbEolFetchedAt] = useState<string | null>(null);
    const [dbEolFetchError, setDbEolFetchError] = useState<boolean>(false);

    // Use instances from context (already loaded with tags by Content) to avoid duplicate /tags API calls
    const { instances: contextInstances } = useInstanceTargets();

    // Use custom hook for instance filtering
    const {
        filters,
        updateFilter,
        clearAllFilters,
        instances,
        availableFilterOptions,
        availableHostTypes,
        getFilteredInstances,
        filtersLoading
    } = useInstanceFilters({
        instances: contextInstances,
        shouldFetch: false
    });

    // Fetch fetched_at from remote db-eol.json, fallback to local if needed
    useEffect(() => {
        fetch("https://db-versions.dbmarlin.com/api/db-eol.json")
                .then(res => res.json())
                .then(data => {
                    if (data && data.fetched_at) {
                        setDbEolFetchedAt(data.fetched_at);
                    } else {
                        throw new Error("No fetched_at in remote");
                    }
                })
                .catch(() => {
                    // Try local as fallback
                    fetch("/api/db-eol.json")
                            .then(res => res.json())
                            .then(data => {
                                if (data && data.fetched_at) {
                                    setDbEolFetchedAt(data.fetched_at);
                                } else {
                                    setDbEolFetchError(true);
                                }
                            })
                            .catch(() => setDbEolFetchError(true));
                });
    }, []);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            setError(null);
            try {
                // 1. Fetch datasources
                const datasources = await InstanceApi.getInstances();

                // 2. Fetch public DB versions
                const resp = await fetch("https://db-versions.dbmarlin.com/api/db-eol.json");
                const dbEolData = await resp.json();

                // 2b. Fetch SQL Server versions JSON
                let sqlServerJson: any = null;
                try {
                    const sqlServerResp = await fetch("https://db-versions.dbmarlin.com/api/sqlserverversions.json");
                    sqlServerJson = await sqlServerResp.json();
                } catch (err) {
                    // If fetch fails, leave as null and fallback to db-eol.json logic for SQL Server
                    sqlServerJson = null;
                }

                // 3. Join logic
                const joined = datasources.map((ds: any) => {
                    try {
                        let mappedType = DB_TYPE_MAP[ds.type] || ds.type;
                        let dbVersion = ds.version;
                        // MariaDB detection: version ends with -MariaDB
                        if (/-MariaDB$/i.test(ds.version)) {
                            mappedType = "MariaDB";
                            dbVersion = ds.version.replace(/-MariaDB$/i, "");
                        }
                        const normVersion = normalizeVersion(
                                mappedType.toLowerCase(),
                                dbVersion
                        );

                        // Special case for SQL Server
                        if (mappedType === "Microsoft SQL Server" && sqlServerJson?.data?.versions) {
                            const sqlServerEntry = sqlServerJson.data.versions.find((v: any) => v.Version === dbVersion);

                            if (sqlServerEntry) {
                                const sqlLabel = `${sqlServerEntry.Edition} ${sqlServerEntry.ProductLevel}`.trim();
                                const majorVersion = dbVersion.match(/^(\d+\.\d+)/)?.[1] || "";
                                const spMatch = sqlServerEntry.ProductLevel?.match(/SP(\d+)/i);
                                const spSuffix = spMatch ? `-sp${spMatch[1]}` : "";

                                const eolEntry = dbEolData.data?.["Microsoft SQL Server"]
                                        ? findSqlServerEolEntry(dbEolData.data["Microsoft SQL Server"], getSqlServerBaseLabel(sqlLabel), majorVersion, spSuffix)
                                        : null;

                                return {
                                    id: ds.id,
                                    name: ds.name,
                                    dbType: mappedType,
                                    label: sqlLabel,
                                    dbVersion,
                                    releaseDate: sqlServerEntry.ReleaseDate || "",
                                    latestUpdate: eolEntry?.latest?.name || "",
                                    latestUpdateLink: eolEntry?.latest?.link || "",
                                    latestUpdateReleaseDate: eolEntry?.latest?.date || "",
                                    labelUrl: sqlServerEntry.DownloadUrl || "",
                                    endOfMainstreamSupport: sqlServerEntry.EndOfSupportDate || "",
                                    endOfExtendedSupport: sqlServerEntry.EndOfExtendedSupportDate || "",
                                    longTermSupport: "",
                                    endOfLife: sqlServerEntry.EndOfExtendedSupportDate || "",
                                    isMaintained: eolEntry?.is_maintained !== undefined ? (eolEntry.is_maintained ? "Y" : "N") : ""
                                };
                            }
                            // If not found, fall through to db-eol.json logic
                        }

                        // Find best match in public data
                        const versionsArr = dbEolData.data && dbEolData.data[mappedType];
                        let versionEntry = null;
                        if (Array.isArray(versionsArr)) {
                            // Special matching for SAP HANA, SAP ASE, Informix, Db2
                            if (mappedType === "SAP HANA Database") {
                                // Mask last segment to .xxxxxxxx for matching
                                const maskHana = (v: string) => v.replace(/\.\d+$/, ".xxxxxxxx");
                                const maskedNorm = maskHana(normVersion);
                                versionEntry = versionsArr.find((v: any) =>
                                        v.version === maskedNorm ||
                                        maskHana(dbVersion) === v.version
                                );
                            } else if (mappedType === "SAP ASE Database") {
                                // Extract version like "16.0 SP04 PL06" from dbVersion string if possible
                                let aseVersion = dbVersion;
                                // Try to extract "16.0 SPxx PLxx" or "16.0" from the string
                                const aseMatch = dbVersion && dbVersion.match(/(\d+\.\d+)(?:\s*SP\d+\s*PL\d+)?/);
                                if (aseMatch) {
                                    // Try to reconstruct the version string as in db-eol.json
                                    const spPlMatch = dbVersion.match(/(SP\d+\s*PL\d+)/);
                                    if (spPlMatch) {
                                        aseVersion = `${aseMatch[1]} ${spPlMatch[1]}`;
                                    } else {
                                        aseVersion = aseMatch[1];
                                    }
                                }
                                versionEntry = versionsArr.find((v: any) =>
                                        v.version === aseVersion ||
                                        v.version === dbVersion ||
                                        v.version === normVersion ||
                                        (v.version && dbVersion && dbVersion.startsWith(v.version)) ||
                                        (v.version && aseVersion && aseVersion.startsWith(v.version))
                                );
                                if (!versionEntry) {
                                    // Try matching just major.minor
                                    const majorMinor = normVersion.split(".").slice(0, 2).join(".");
                                    versionEntry = versionsArr.find((v: any) =>
                                            v.version === majorMinor
                                    );
                                }
                            } else if (mappedType === "Informix Server" || mappedType === "Db2 LUW") {
                                // For Db2, prioritize exact major.minor match, then fall back
                                let db2MajorMinorMinor = "";
                                let db2MajorMinor = normVersion;
                                if (mappedType === "Db2 LUW" && typeof dbVersion === "string") {
                                    try {
                                        const clean = dbVersion.replace(/^DB2\s+/i, "").replace(/^v/i, "").replace(/^[^\d]*/, "");
                                        const parts = clean.split(".");
                                        if (parts.length >= 3) {
                                            db2MajorMinorMinor = `${parts[0]}.${parts[1]}.${parts[2]}`;
                                        }
                                        if (parts.length >= 2) {
                                            db2MajorMinor = `${parts[0]}.${parts[1]}`;
                                        }
                                    } catch (e) {
                                        db2MajorMinorMinor = "";
                                    }
                                }
                                // 1. Try exact major.minor match
                                versionEntry = versionsArr.find((v: any) =>
                                        v.version === db2MajorMinor
                                );
                                // 2. Try major.minor.minor
                                if (!versionEntry && db2MajorMinorMinor) {
                                    versionEntry = versionsArr.find((v: any) =>
                                            v.version === db2MajorMinorMinor
                                    );
                                }
                                // 3. Try normVersion, dbVersion, and fallback strategies
                                if (!versionEntry) {
                                    versionEntry = versionsArr.find((v: any) =>
                                            v.version === normVersion ||
                                            v.version === dbVersion ||
                                            (v.version && normVersion && normVersion.startsWith(v.version)) ||
                                            (v.version && dbVersion && dbVersion.startsWith(v.version)) ||
                                            (normVersion && v.version && v.version.startsWith(normVersion)) ||
                                            (dbVersion && v.version && v.version.startsWith(dbVersion))
                                    );
                                }
                            } else {
                                // Default logic
                                versionEntry = versionsArr.find((v: any) => {
                                    return (
                                            v.version === normVersion ||
                                            v.version === dbVersion ||
                                            v.version === normVersion.split(".")[0]
                                    );
                                });
                            }
                        }

                        return {
                            id: ds.id,
                            name: ds.name,
                            dbType: mappedType,
                            label: versionEntry?.label || ds.label || "",
                            dbVersion: dbVersion,
                            releaseDate: versionEntry?.release_date || "",
                            latestUpdate: versionEntry?.latest?.name || "",
                            latestUpdateLink: versionEntry?.latest?.link || "",
                            latestUpdateReleaseDate: versionEntry?.latest?.date || "",
                            endOfMainstreamSupport: versionEntry?.eol || "",
                            endOfExtendedSupport: versionEntry?.eoes || "",
                            longTermSupport: versionEntry?.is_lts ? "Y" : "",
                            endOfLife: versionEntry?.eol || "",
                            isMaintained: versionEntry ? (versionEntry.is_maintained ? "Y" : "N") : ""
                        };
                    } catch (innerError: any) {
                        // Return a fallback object for problematic datasources
                        return {
                            id: ds.id,
                            name: ds.name,
                            dbType: DB_TYPE_MAP[ds.type] || ds.type,
                            label: ds.label || "",
                            dbVersion: ds.version || "",
                            releaseDate: "",
                            latestUpdate: "",
                            latestUpdateLink: "",
                            latestUpdateReleaseDate: "",
                            endOfMainstreamSupport: "",
                            endOfExtendedSupport: "",
                            longTermSupport: "",
                            endOfLife: "",
                            isMaintained: ""
                        };
                    }
                });

                setRows(joined);
            } catch (e: any) {
                setError("Failed to load DB Versions");
            }
            setLoading(false);
        }

        fetchData();
    }, []);

    // Get filtered instance IDs using the hook's helper
    const filteredInstanceIds = useMemo(() => {
        if (instances.length === 0) {
            return null; // No filtering if instances not loaded yet
        }
        const filtered = getFilteredInstances();
        return new Set(filtered.map(inst => inst.id));
    }, [instances, getFilteredInstances]);

    // Filtered rows based on search, maintainedFilter, selectedDbType, and instance filters
    const filteredRows = useMemo(() => {
        let filtered = rows as any[];

        // Apply instance filters first
        if (filteredInstanceIds !== null) {
            filtered = filtered.filter((row: any) => filteredInstanceIds.has(row.id));
        }

        if (maintainedFilter === "supported") {
            filtered = filtered.filter((row: any) => row.isMaintained === "Y");
        } else if (maintainedFilter === "unsupported") {
            filtered = filtered.filter((row: any) => row.isMaintained === "N");
        }
        if (selectedDbType) {
            filtered = filtered.filter((row: any) => row.dbType === selectedDbType);
        }
        if (!search) return filtered;
        const lower = search.toLowerCase();
        return filtered.filter((row: any) =>
                (row.name && row.name.toLowerCase().includes(lower)) ||
                (row.dbType && row.dbType.toLowerCase().includes(lower)) ||
                (row.label && row.label.toLowerCase().includes(lower)) ||
                (row.dbVersion && row.dbVersion.toLowerCase().includes(lower))
        );
    }, [rows, search, maintainedFilter, selectedDbType, filteredInstanceIds]);

    // Prepare data for DB Type PieChart (using filtered rows)
    const dbTypeChartData = useMemo(() => {
        const counts: Record<string, { count: number; color?: string }> = {};
        filteredRows.forEach((row: any) => {
            if (!counts[row.dbType]) counts[row.dbType] = { count: 0 };
            counts[row.dbType].count += 1;
        });
        // Use a fixed color palette for consistency
        const palette = [
            "#7cb5ec", "#90ed7d", "#f7a35c", "#8085e9", "#f15c80",
            "#e4d354", "#2b908f", "#f45b5b", "#91e8e1", "#434348"
        ];
        // Sort by count in descending order (most popular first)
        return Object.entries(counts)
                .sort(([, a], [, b]) => b.count - a.count)
                .map(([dbType, { count }], i) => ({
                    name: dbType,
                    y: count,
                    color: palette[i % palette.length],
                    formatted: count.toString(),
                    selected: selectedDbType === dbType // highlight the selected segment
                }));
    }, [filteredRows, selectedDbType]);

    // Prepare data for Label BarChart (using filtered rows and selectedDbType)
    const labelChartData = useMemo(() => {
        const counts: Record<string, { count: number; color?: string }> = {};
        filteredRows.forEach((row: any) => {
            if (selectedDbType && row.dbType !== selectedDbType) return;
            const label = row.label || "Unknown";
            if (!counts[label]) counts[label] = { count: 0 };
            counts[label].count += 1;
        });
        const palette = [
            "#7cb5ec", "#90ed7d", "#f7a35c", "#8085e9", "#f15c80",
            "#e4d354", "#2b908f", "#f45b5b", "#91e8e1", "#434348"
        ];
        return Object.entries(counts).map(([label, { count }], i) => ({
            name: label,
            y: count,
            color: palette[i % palette.length],
            formatted: count.toString()
        }));
    }, [filteredRows, selectedDbType]);

    function handleClear() {
        setSearch("");
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }

    // Table columns for react-table
    const columns: Column<any>[] = useMemo(() => [
        {
            Header: "Name",
            accessor: "name",
            style: { minWidth: 260, width: 320 },
            Cell: ({ row }) => (
                    <a href={`/instances/${row.original.id}/activity`}>{row.original.name}</a>
            )
        },
        {
            Header: "DB Type",
            accessor: "dbType"
        },
        {
            Header: "DB Version",
            accessor: "dbVersion",
            style: { width: 60 },
            Cell: ({ value }) => value || "-"
        },
        {
            Header: "Label",
            accessor: "label",
            Cell: ({ row }) => {
                const label = row.original.label || "-";
                // For SQL Server, if labelUrl is present, render as hyperlink
                if (
                        row.original.dbType === "Microsoft SQL Server" &&
                        row.original.labelUrl
                ) {
                    return (
                            <a href={row.original.labelUrl} target="_blank" rel="noopener noreferrer">
                                {label}
                            </a>
                    );
                }
                return label;
            }
        },
        {
            Header: "Release Date",
            accessor: "releaseDate",
            Cell: ({ value }) => value || "-"
        },
        {
            Header: "Latest available update",
            accessor: "latestUpdate",
            Cell: ({ row }) => row.original.latestUpdateLink ? (
                    <a href={row.original.latestUpdateLink} target="_blank" rel="noopener noreferrer">
                        {row.original.latestUpdate || row.original.latestUpdateLink}
                    </a>
            ) : (
                    row.original.latestUpdate || "-"
            )
        },
        {
            Header: "Latest update release date",
            accessor: "latestUpdateReleaseDate",
            Cell: ({ value }) => value || "-"
        },
        {
            Header: "End of mainstream support",
            accessor: "endOfMainstreamSupport",
            Cell: ({ value }) => value || "-"
        },
        {
            Header: "End of extended support",
            accessor: "endOfExtendedSupport",
            Cell: ({ value }) => value || "-"
        },
        {
            Header: "Long Term Support",
            accessor: "longTermSupport",
            Cell: ({ value }) =>
                    value === "Y" ? "Yes" : value === "N" ? "No" : "-"
        },
        {
            Header: "End of Life",
            accessor: "endOfLife",
            Cell: ({ value }) => value || "-"
        },
        {
            Header: "Is Maintained?",
            accessor: "isMaintained",
            Cell: ({ value }) =>
                    value === "N" ? (
                            <span style={{ background: "gold", fontWeight: "bold" }}>No</span>
                    ) : value === "Y" ? (
                            "Yes"
                    ) : (
                            "-"
                    )
        }
    ], []);

    // Pagination: get default page size from localStorage or fallback to 20
    function getDefaultPageSize() {
        const stored = window.localStorage.getItem('pageSize');
        const parsed = stored ? parseInt(stored, 10) : null;
        return parsed && !isNaN(parsed) ? parsed : 20;
    }

    const tableInstance = useTable(
            {
                columns,
                data: filteredRows,
                initialState: {
                    pageIndex: 0,
                    pageSize: getDefaultPageSize()
                }
            },
            useSortBy,
            usePagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page, // instead of rows: tableRows
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
        prepareRow
    } = tableInstance;

    // Helper to format date as "16 May 2024"
    function formatFetchedAt(dateStr: string | null): string {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    }

    return (
            <PageContent
                    title="DB Versions - DBmarlin"
                    description="List of all monitored DB versions and their support status"
            >
                {/* Header and Breadcrumbs */}
                <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                    <Breadcrumb heading="DB Versions">
                        <Link to="/db-versions">Analysis</Link>
                        <span className="current-breadcrumb-link">DB Versions</span>
                    </Breadcrumb>
                </div>
                <div className="row">
                    <div className="col">

                        {/* Callout below breadcrumbs, above table panel */}
                        <div className="alert alert-info d-flex align-items-center py-2 px-3 mb-3" role="alert" style={{ fontSize: 15 }}>
                            <i className="fas fa-info-circle me-2" aria-hidden="true"/>
                            <span>
          The data in the Latest available update column was correct as of{" "}
                                {dbEolFetchedAt
                                        ? formatFetchedAt(dbEolFetchedAt)
                                        : dbEolFetchError
                                                ? "date unavailable"
                                                : "…"
                                }.
                                {" "}
                                <a href="https://db-versions.dbmarlin.com" target="_blank" rel="noopener noreferrer">More info</a>
        </span>
                        </div>
                    </div>
                </div>

                {/* Filter Panel */}
                {instances.length > 0 && (
                        <div className="row">
                            <div className="col">
                                <FilterPanel
                                        filterGroups={[
                                            {
                                                name: 'sensorstatus',
                                                label: 'Sensor Status',
                                                options: availableFilterOptions.sensorstatuses,
                                                selectedValues: filters.sensorstatus || [],
                                                onSelectionChange: (values: string[]) => updateFilter('sensorstatus', values)
                                            },
                                            {
                                                name: 'type',
                                                label: 'Type',
                                                options: availableFilterOptions.types,
                                                selectedValues: filters.type,
                                                onSelectionChange: (values: string[]) => updateFilter('type', values)
                                            },
                                            {
                                                name: 'tag',
                                                label: 'Tag',
                                                options: availableFilterOptions.tags,
                                                selectedValues: filters.tag,
                                                onSelectionChange: (values: string[]) => updateFilter('tag', values)
                                            },
                                            {
                                                name: 'host',
                                                label: 'Host',
                                                options: availableFilterOptions.hosts,
                                                selectedValues: filters.host,
                                                onSelectionChange: (values: string[]) => updateFilter('host', values)
                                            },
                                            {
                                                name: 'hostType',
                                                label: 'Host Type',
                                                options: availableHostTypes,
                                                selectedValues: filters.hostType,
                                                onSelectionChange: (values: string[]) => updateFilter('hostType', values)
                                            },
                                            {
                                                name: 'agent',
                                                label: 'Agent',
                                                options: availableFilterOptions.agents,
                                                selectedValues: filters.agent || [],
                                                onSelectionChange: (values: string[]) => updateFilter('agent', values)
                                            }
                                        ]}
                                        onClearAll={clearAllFilters}
                                        isLoading={filtersLoading}
                                />
                            </div>
                        </div>
                )}

                {/* Clear Filter Button */}
                {selectedDbType && (
                        <div className="row mb-3">
                            <div className="col">
                                <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setSelectedDbType(null)}
                                >
                                    <i className="far fa-times me-1"></i>
                                    Clear Filter: {selectedDbType}
                                </button>
                            </div>
                        </div>
                )}

                {/* Charts Row */}
                <div className="row mb-4">
                    <div className="col-lg-6 col-md-12 mb-3 mb-lg-0">
                        <ConditionalRender if={loading}>
                            <div className="col text-center text-muted mt-3 pie-chart-loader">
                                <div className="loader spinner">
                                </div>
                                <p className="mt-3">
                                    Loading data...
                                </p>
                            </div>
                        </ConditionalRender>
                        <ConditionalRender if={!loading}>
                            <PieChart
                                    filtersOptions={[]} // Not used for this context
                                    uniqueIndex={0}
                                    chartOptions={[
                                        { optionId: 0, key: "DB Type", data: dbTypeChartData }
                                    ]}
                                    hideOptions={true}
                                    defaultChartOption={{ optionId: 0, key: "DB Type", data: dbTypeChartData }}
                                    setFilterOptions={noOpSetFilterOptions}
                                    instanceType=""
                                    onSliceClick={handleDbTypeSliceClick}
                                    title={
                                        selectedDbType
                                                ? `Database Type Breakdown (${selectedDbType})`
                                                : "Database Type Breakdown"
                                    }
                                    customTooltipFormatter={(point: any) => `
                                        <div>
                                            <b>${point.name}</b>
                                            <div class="d-flex justify-content-between">
                                                <span>Total instances:</span>
                                                <b style="padding-left: 20px">${point.formatted}</b>
                                            </div>
                                            <div class="d-flex justify-content-between">
                                                <span>Percentage:</span>
                                                <b style="padding-left: 20px">${point.percentage.toFixed(0)}%</b>
                                            </div>
                                        </div>`
                                    }
                            />
                        </ConditionalRender>

                    </div>
                    <div className="col-lg-6 col-md-12">
                        <ConditionalRender if={loading}>
                            <div className="col text-center text-muted mt-3 pie-chart-loader">
                                <div className="loader spinner">
                                </div>
                                <p className="mt-3">
                                    Loading data...
                                </p>
                            </div>
                        </ConditionalRender>
                        <ConditionalRender if={!loading}>
                            <BarChart
                                    uniqueIndex={1}
                                    chartOption={{ optionId: 0, key: "Label", data: labelChartData }}
                                    title={
                                        selectedDbType
                                                ? `Breakdown by Label (${selectedDbType})`
                                                : "Breakdown by Label"
                                    }
                            />
                        </ConditionalRender>
                    </div>
                </div>
                <div className="row row-cols-1">
                    <div className="col">
                        <div className="card collapsible">
                            <div className="card-header">
                                <i className="fal fa-database fa-fw" aria-hidden="true"/>
                                DB Versions
                                <span className="badge bg-info"
                                      data-tip="Total DB versions count">{filteredRows.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                            </div>
                            <div className="card-body">
                                <div className="row table-search align-items-center">
                                    <div className="col-auto">
                                        {/* Filter Buttons */}
                                        <FilterButtonGroup
                                                options={[
                                                    { label: 'All', value: 'all', variant: 'primary' },
                                                    { label: 'Supported', value: 'supported', variant: 'success', icon: 'fa-check-circle' },
                                                    { label: 'Unsupported', value: 'unsupported', variant: 'danger', icon: 'fa-times-circle' }
                                                ]}
                                                selectedValue={maintainedFilter}
                                                onChange={setMaintainedFilter}
                                                ariaLabel="Support filter"
                                        />
                                    </div>
                                    <div className="col-auto ms-auto">
                                        <TableCSVExport
                                            tableInstance={tableInstance}
                                            filename="DB Versions"
                                        />
                                    </div>

                                    <div className="col-md-2">
                                        <SearchInput
                                                ref={inputRef}
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                onClear={handleClear}
                                        />
                                    </div>
                                </div>
                                <NoData
                                        error={error}
                                        loading={loading}
                                        length={filteredRows.length}
                                />
                                {!loading && !error && (
                                        <div className="table-responsive">
                                            <table className="table" {...getTableProps()}>
                                                <thead>
                                                {headerGroups.map(headerGroup => (
                                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                                            {headerGroup.headers.map(column => (
                                                                    <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                                                        className={(column as any).className}>
                                                                        {column.render('Header')}
                                                                        {column.canSort ? (column.isSorted ? (column.isSortedDesc ?
                                                                                        <i className="fal fa-sort-amount-up-alt"></i> :
                                                                                        <i className="fal fa-sort-amount-down-alt"></i>) :
                                                                                <i className="fal fa-sort-amount-down-alt text-light"></i>) : ''}
                                                                    </th>
                                                            ))}
                                                        </tr>
                                                ))}
                                                </thead>

                                                <tbody {...getTableBodyProps()}>
                                                {page.map(row => {
                                                    prepareRow(row);
                                                    return (
                                                            <tr {...row.getRowProps()}>
                                                                {row.cells.map(cell => (
                                                                        <td
                                                                                {...cell.getCellProps()}
                                                                                style={(cell.column as any).style || {}}
                                                                        >
                                                                            {cell.render("Cell")}
                                                                        </td>
                                                                ))}
                                                            </tr>
                                                    );
                                                })}
                                                </tbody>
                                            </table>
                                            {filteredRows.length === 0 && (
                                                    <div className="text-center text-muted py-4">
                                                        No data found.
                                                    </div>
                                            )}
                                            <TablePagination
                                                    length={filteredRows.length}
                                                    pageSize={pageSize}
                                                    setPageSize={setPageSize}
                                                    pageOptions={pageOptions}
                                                    pageCount={pageCount}
                                                    canPreviousPage={canPreviousPage}
                                                    previousPage={previousPage}
                                                    canNextPage={canNextPage}
                                                    nextPage={nextPage}
                                                    gotoPage={gotoPage}
                                                    pageIndex={pageIndex}
                                            />
                                        </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </PageContent>
    );
};

export default DBVersionsList;
